import { MenuItem } from "@mui/material";
import React, { useEffect, useState } from "react";
import { Field } from "react-final-form";
import UiInput from "../../../_uiComponents/UiInput";
import { UiSelectInput } from "../../../_uiComponents/common/UiSelectInput";
import { UiButton } from "../../../_uiComponents/common/UiButton";
import { Form } from "react-final-form";
import * as yup from "yup";
import Validator from "../../../../_helpers/validate.schema";
import { useDispatch, useSelector } from "react-redux";
import {
  clearForgotDetails,
  forgotUsernameOTPConfirmRequest,
  forgotUsernameOTPResendRequest,
  forgotUsernameValidateRequest,
} from "../../../../_redux/_actions";
import { useTranslation } from "react-i18next";
import { OtpConfirmModal } from "../../auth/views/OtpConfirmModal";
import { useNavigate } from "react-router-dom";
import {
  accountTypesConstants,
  authConstants,
  commonConstants,
} from "../../../../_redux/_constants";
import {
  formatNicNumberInput,
  formatCanNumberInput,
  formatAccountNumber,
  validPassportNumber,
  showCustomLoading,
} from "../../../../../public/_helpers/support.services";
import { isValidExpiryDate } from "../../../../../public/_helpers/dateFormatter";
import { UiBackdropLoader } from "../../../_uiComponents/common/UiGrayBgLoader";

export const ForgotUsernameForm = (props) => {
  const { onDirtyChange } = props;
  const { t, i18n } = useTranslation();

  const validateSchema = () =>
    yup.object().shape({
      accountType: yup.string().required("Account Type is required."),
      bankAccountNo: yup.string().required("Bank Account Number is required."),
      personalIdTypeCode: yup
        .string()
        .required("Identification Type is required."),
      nicNumber: yup.string().when("personalIdTypeCode", {
        is: commonConstants.DOCUMENT_TYPE_NATIONAL_ID,
        then: (schema) =>
          schema
            .matches(
              /^(\d{9}[VvXx]|\d{12})$/,
              "NIC number must be in the correct format (9 digits + X/V or 12 digits)",
            )
            .min(10, "NIC number must be minimum 10 characters long")
            .max(12, "NIC number must not exceed 12 digits")
            .required(t("This field is required")),
        otherwise: (schema) => schema.notRequired(),
      }),

      passportNumber: yup.string().when("personalIdTypeCode", {
        is: commonConstants.DOCUMENT_TYPE_PASSPORT_ID,
        then: (schema) =>
          schema
            .matches(
              /^[A-Za-z0-9]{6,12}$/,
              "Please enter a valid passport number.",
            )
            .required(t("This field is required")),
        otherwise: (schema) => schema.notRequired(),
      }),
    });

  const [modalOpen, setModalOpen] = useState(false);
  const [userData, setUserData] = useState(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const onValidate = async (values) => {
    return await Validator(values, validateSchema);
  };
  const [expiryDateError, setExpiryDateError] = useState(false);
  const [nicNumberError, setNicNumberError] = useState(false);
  const [passportNumberError, setPassportNumberError] = useState(false);
  const [canNumberError, setCanNumberError] = useState(false);
  const [bankAccountNoError, setBankAccountNoError] = useState(null);
  const [isNextEnabled, setIsNextEnabled] = useState(false);
  const { auth, loader } = useSelector((state) => {
    return {
      auth: state.auth,
      loader: state.loading,
    };
  });

  useEffect(() => {
    if (auth?.forgotUserNameOtpTimer) {
      setModalOpen(true);
    } else {
      setModalOpen(false);
    }
  }, [auth?.forgotDetails]);

  useEffect(() => {
    if (auth?.isForgotFlowResetSuccess) {
      navigate("/");
    }
  }, [auth?.isForgotFlowResetSuccess]);

  const closeOtpModal = () => {
    dispatch(clearForgotDetails());
    setModalOpen(false);
  };

  const submit = (data) => {
    setUserData(data);

    let payload = {
      personalIdTypeCode: data?.personalIdTypeCode,
      personalId:
        data?.personalIdTypeCode === commonConstants.DOCUMENT_TYPE_NATIONAL_ID
          ? data?.nicNumber
          : data?.passportNumber,
      registeredType: data?.accountType,
      bankAccountNo: data?.bankAccountNo,
      accountType:
        data?.accountType === accountTypesConstants.CASA_ACCOUNT_TYPE
          ? accountTypesConstants.SAVING_ACCOUNT_TYPE
          : data?.accountType,
    };

    dispatch(forgotUsernameValidateRequest(payload));
  };

  const otpConfirm = (values) => {
    dispatch(
      forgotUsernameOTPConfirmRequest({
        personalId:
          userData?.personalIdTypeCode ===
          commonConstants.DOCUMENT_TYPE_NATIONAL_ID
            ? userData?.nicNumber
            : userData?.passportNumber,
        personalIdTypeCode: userData?.personalIdTypeCode,
        ...values,
      }),
    );
  };

  const otpResend = (sendType) => {
    dispatch(
      forgotUsernameOTPResendRequest({
        personalId:
          userData?.personalIdTypeCode ===
          commonConstants.DOCUMENT_TYPE_NATIONAL_ID
            ? userData?.nicNumber
            : userData?.passportNumber,
        personalIdTypeCode: userData?.personalIdTypeCode,
        sendType: sendType,
      }),
    );
  };

  return (
    <>
      <Form
        onSubmit={submit}
        validate={onValidate}
        initialValues={{
          accountType: accountTypesConstants.CASA_ACCOUNT_TYPE,
          personalIdTypeCode: commonConstants.DOCUMENT_TYPE_NATIONAL_ID,
        }}
        render={({
          handleSubmit,
          values,
          submitting,
          hasValidationErrors,
          form,
        }) => {
          useEffect(() => {
            setIsNextEnabled(!hasValidationErrors);
          }, [hasValidationErrors]);

          useEffect(() => {
            form.change(
              "personalIdTypeCode",
              commonConstants.DOCUMENT_TYPE_NATIONAL_ID,
            );
            form.change("accountType", accountTypesConstants.CASA_ACCOUNT_TYPE);
          }, []);

          useEffect(() => {
            const unsubscribe = form.subscribe(
              ({ dirty }) => {
                onDirtyChange?.(dirty);
              },
              { dirty: true },
            );

            return () => unsubscribe();
          }, [form]);

          return (
            <form>
              <UiSelectInput
                name="accountType"
                label={t("Account Type")}
                id="inputAccountType"
                form={form}
                value={values.accountType}
              >
                {accountTypesConstants.REGISTERED_ACCOUNT_TYPE_LIST.map(
                  (option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ),
                )}
              </UiSelectInput>

              <UiInput
                name="bankAccountNo"
                id="inputbankAccountNo"
                label={`${t("Account Number")} *`}
                type="text"
                maxLength={16}
                min={0}
                clearable={true}
                form={form}
                value={values.bankAccountNo}
                inputonChange={(e) => {
                  form.change("bankAccountNo", e.target.value);
                }}
                error={!!bankAccountNoError}
                helperText={bankAccountNoError ? bankAccountNoError : null}
              />

              <UiSelectInput
                label={`${t("Identification Document Type")} *`}
                id="personalIdTypeCode"
                name="personalIdTypeCode"
                form={form}
                value={values.personalIdTypeCode}
              >
                <MenuItem value={commonConstants.DOCUMENT_TYPE_NATIONAL_ID}>
                  {t("NIC")}
                </MenuItem>
                <MenuItem value={commonConstants.DOCUMENT_TYPE_PASSPORT_ID}>
                  {t("Passport")}
                </MenuItem>
              </UiSelectInput>

              {values.personalIdTypeCode ==
              commonConstants.DOCUMENT_TYPE_NATIONAL_ID ? (
                <UiInput
                  name="nicNumber"
                  id="inputNicNumber"
                  label={`${t("NIC Number")} *`}
                  type="text"
                  min={0}
                  clearable={true}
                  maxLength={12}
                  value={values.nicNumber}
                  inputonChange={(e) => {
                    const rawInput = e.target.value.replace(/\s/g, "");
                    let cleaned = rawInput.replace(/[^0-9VXvx]/g, "");
                    if (cleaned.length > 12) cleaned = cleaned.slice(0, 12);
                    cleaned = cleaned
                      .split("")
                      .filter((char, index) => {
                        if (index < 9) return /[0-9]/.test(char);
                        if (index === 9) return /[0-9VXvx]/.test(char);
                        if (index > 9) return /[0-9]/.test(char);
                        return false;
                      })
                      .join("");

                    const result = formatNicNumberInput(cleaned);

                    if (result.value === "" || result.isValid) {
                      setNicNumberError(false);
                    } else {
                      setNicNumberError(result.message);
                    }

                    form.change("nicNumber", result.value);
                  }}
                  error={nicNumberError}
                  helperText={nicNumberError ? nicNumberError : null}
                />
              ) : (
                <>
                  <UiInput
                    name="passportNumber"
                    id="inputPassportNumber"
                    label={`${t("Passport Number")} *`}
                    type="text"
                    min={0}
                    clearable={true}
                    maxLength={12}
                    form={form}
                    value={values.passportNumber}
                    inputonChange={(passportNumber) => {
                      const digits = passportNumber.target.value
                        .replace(/[^0-9A-Za-z]/g, "")
                        .slice(0, 12);

                      const result = validPassportNumber(digits);
                      if (digits === "" || result.isValid) {
                        setPassportNumberError(false);
                      } else {
                        setPassportNumberError(result.message);
                      }
                      form.change("passportNumber", digits);
                    }}
                    error={passportNumberError}
                    helperText={
                      passportNumberError ? passportNumberError : null
                    }
                  />
                </>
              )}

              <UiButton
                className="df-ui-dark-bg-btn w-100 mt-2"
                name={t("Next")}
                disabled={!isNextEnabled || submitting}
                onClick={handleSubmit}
              />
            </form>
          );
        }}
      />

      <OtpConfirmModal
        show={modalOpen}
        userDetails={auth?.forgotDetails?.customer}
        otpConfirmRequest={otpConfirm}
        otpResendRequest={otpResend}
        closeModal={closeOtpModal}
        countDownTimer={auth?.forgotUserNameOtpTimer}
      />

      {showCustomLoading(
        [authConstants.FORGOT_USERNAME_OTP_CONFIRM_REQUEST],
        loader.pendingRequests,
      ) && (
        <UiBackdropLoader
          loaderSize={40}
          backdropMessage={t("Recovering your username...")}
          inverted
        />
      )}
    </>
  );
};
